// Home Port country/state, as Salesforce defines them.
//
// Both are RESTRICTED picklists on Boat__c, and Home_Port_State__c is also a
// DEPENDENT one: a state that is perfectly valid on its own is still rejected
// under the wrong country. Salesforce refuses the whole record, Heroku Connect
// reports it long after the API answered 200, and the boat is left in Postgres
// with a NULL sfid that nobody sees.
//
// The form used to offer one flat list of US states no matter which country was
// picked, so choosing Canada guaranteed a rejected boat. Six are stuck in
// production that way. Grouping the states by their controlling country is what
// stops that pair from ever being offered.
//
// This mirrors COUNTRY_TO_STATES in the API's common/home_port.py, which was
// captured from the production Boat__c field describe. The API normalizes
// whatever reaches it; this keeps the form from sending something it has to.

export const STATES_BY_COUNTRY = {
  "United States": [
    "Alabama",
    "Alaska",
    "American Samoa",
    "Arizona",
    "Arkansas",
    "Armed Forces Americas",
    "Armed Forces Europe",
    "Armed Forces Pacific",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    // Salesforce spells this with a lower-case "of". The form offered
    // "District Of Columbia", which is not a picklist entry, so every boat
    // recorded in DC through this form was rejected: 94 boats carry the real
    // value and not one carries the capitalised one.
    "District of Columbia",
    "Federated Micronesia",
    "Florida",
    "Georgia",
    "Guam",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Marshall Islands",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Northern Mariana Islands",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Palau",
    "Pennsylvania",
    "Puerto Rico",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "US Virgin Islands",
    "United States Minor Outlying Islands",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    // Unaccented. 22 boats spelled "Québec" are stuck unsynced; the 18 that
    // reached Salesforce all spell it this way.
    "Quebec",
    "Saskatchewan",
    "Yukon Territories",
  ],
  Mexico: [
    "Aguascalientes",
    "Baja California",
    "Baja California Sur",
    "Campeche",
    "Chiapas",
    "Chihuahua",
    "Coahuila",
    "Colima",
    "Durango",
    "Federal District",
    "Guanajuato",
    "Guerrero",
    "Hidalgo",
    "Jalisco",
    "Mexico State",
    "Michoacán",
    "Morelos",
    "Nayarit",
    "Nuevo León",
    "Oaxaca",
    "Puebla",
    "Querétaro",
    "Quintana Roo",
    "San Luis Potosí",
    "Sinaloa",
    "Sonora",
    "Tabasco",
    "Tamaulipas",
    "Tlaxcala",
    "Veracruz",
    "Yucatán",
    "Zacatecas",
  ],
  Panama: ["Panama"],
  // Tortola is the state; British Virgin Islands is the country it sits under.
  "British Virgin Islands": ["Tortola"],
  // Valid countries that carry no states of their own.
  Bahamas: [],
  Other: [],
};

// The states Salesforce will accept under this country, as select options.
// The countries this file can pair a state with, in the order they should be
// offered. The boat card used to borrow the billing address list, which holds
// only United States and Canada - so 246 boats in Salesforce sit on a country
// the control could not display (Other 150, Bahamas 71, Mexico 14, Panama 7,
// British Virgin Islands 4) and rendered as "United States" instead.
export function homePortCountryOptions() {
  return Object.keys(STATES_BY_COUNTRY).map((country) => ({
    value: country,
    text: country,
  }));
}

// Some countries have no state list at all - Salesforce holds a null state for
// every Bahamas and Other boat in production. Asking for one there is asking
// for a value that does not exist, which is why those records could not be
// submitted: the state select offered nothing but its own placeholder while
// `required` refused to pass.
export function homePortHasStates(country) {
  return homePortStateOptions(country).length > 0;
}

// An unknown country yields nothing rather than the US list, because offering
// a state Salesforce cannot pair with the country is what caused the problem.
export function homePortStateOptions(country) {
  const states = STATES_BY_COUNTRY[country] || [];
  return states.map((state) => ({ value: state, text: state }));
}

export function isValidHomePortState(country, state) {
  if (!state) return true;
  return (STATES_BY_COUNTRY[country] || []).indexOf(state) !== -1;
}

// Whether we can judge a state at all. A record loaded with a country we do not
// recognize gets left alone rather than emptied: the API's normalizer maps the
// odd spellings and territories, and throwing away a state we simply could not
// classify would lose real data the dealer never touched.
export function isKnownHomePortCountry(country) {
  return Object.prototype.hasOwnProperty.call(STATES_BY_COUNTRY, country);
}
